/**
 * Turn an axios error from the custom hotspot / calculation endpoints into a
 * user-facing message.
 *
 * A custom-project validation failure (targeting a non-root activity) returns a
 * structured 422 whose `detail.details.report.roots` names the project's real
 * functional unit. Surface that so the fix is obvious ("re-run targeting X")
 * instead of a generic "Request failed with status code 422".
 */
export function extractHotspotErrorMessage(err: unknown, fallback = 'Calculation failed'): string {
  const anyErr = err as {
    response?: { data?: { detail?: unknown } }
    message?: string
  }
  const detail = anyErr?.response?.data?.detail as
    | {
        message?: string
        details?: {
          error_type?: string
          report?: {
            target?: { activity_code?: string }
            roots?: unknown[]
            errors?: { code?: string }[]
          }
        }
      }
    | string
    | { msg?: string }[]
    | undefined

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const report = detail.details?.report
    if (detail.details?.error_type === 'custom_project_validation_failed' && report) {
      const target = report.target?.activity_code
      const roots = Array.isArray(report.roots)
        ? report.roots
            .map((r) => (Array.isArray(r) ? (r[1] as string) : null))
            .filter((c): c is string => Boolean(c))
        : []
      const nonRoot =
        Array.isArray(report.errors) && report.errors.some((e) => e?.code === 'target_not_root')
      if (nonRoot && roots.length === 1) {
        return `"${target}" is not this project's functional unit. Re-run targeting the root activity "${roots[0]}".`
      }
      if (nonRoot && roots.length > 1) {
        return `"${target}" is not a root. This project has multiple functional-unit candidates: ${roots.join(', ')}. Target one of them.`
      }
    }
    if (typeof detail.message === 'string') return detail.message
  }
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => d?.msg).filter(Boolean)
    if (msgs.length) return msgs.join('; ')
  }
  return anyErr?.message || fallback
}
